import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FiFile, FiX } from 'react-icons/fi';
import { fetchAdminImagePreview } from '@/lib/admin-image-preview';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import { formatPacificDateTime } from '@/lib/adminPacificTime';
import type {
  AdminChatType,
  AdminMessageAttachmentRow,
  AdminMessageAttachmentsListResponse,
} from '@/lib/types';

const PAGE_SIZE = 24;

const ADMIN_USER_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100';

function authorLabel(
  author: AdminMessageAttachmentRow['author'],
  userId: number,
): string {
  const n = [author.first_name, author.last_name].filter(Boolean).join(' ').trim();
  return n || `User #${userId}`;
}

function typeLabel(chatType: AdminChatType): string {
  switch (chatType) {
    case 'main':
      return 'Main';
    case 'custom':
      return 'Custom';
    case 'expense-dispute':
      return 'Expense dispute';
    case 'payment-dispute':
      return 'Payment dispute';
  }
}

function typeBadgeClass(chatType: AdminChatType): string {
  switch (chatType) {
    case 'main':
      return 'bg-sky-100 text-sky-800 border-sky-200';
    case 'custom':
      return 'bg-violet-100 text-violet-800 border-violet-200';
    case 'expense-dispute':
      return 'bg-amber-100 text-amber-900 border-amber-200';
    case 'payment-dispute':
      return 'bg-orange-100 text-orange-900 border-orange-200';
  }
}

function previewPath(attachmentId: number, size: 'thumb' | 'full'): string {
  const params = new URLSearchParams({ size });
  return `/v1/admin/message-attachments/${attachmentId}/image-preview?${params.toString()}`;
}

function AdminAttachmentImagePreview({
  attachment,
  onImagePress,
}: {
  attachment: AdminMessageAttachmentRow;
  onImagePress: (attachment: AdminMessageAttachmentRow) => void;
}) {
  const { getToken } = useAuth();

  const previewQuery = useQuery({
    queryKey: ['admin', 'message-attachment-preview', attachment.id, 'thumb'],
    queryFn: () => fetchAdminImagePreview(getToken, previewPath(attachment.id, 'thumb')),
    enabled: attachment.is_image,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const url = previewQuery.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [previewQuery.data]);

  if (!attachment.is_image) {
    return (
      <div className='flex aspect-square w-full items-center justify-center bg-gray-50'>
        <div className='flex flex-col items-center gap-2 px-3 text-center'>
          <FiFile className='h-8 w-8 text-gray-400' />
          <p className='text-[11px] font-medium text-gray-500 break-all'>
            {attachment.attachment_type || 'File'}
          </p>
        </div>
      </div>
    );
  }

  if (previewQuery.isLoading) {
    return (
      <div
        className='aspect-square w-full bg-gray-100 animate-pulse'
        aria-hidden
      />
    );
  }

  if (previewQuery.isError || !previewQuery.data) {
    return (
      <div className='flex aspect-square w-full items-center justify-center bg-gray-50 px-3 text-center text-xs font-medium text-gray-500'>
        Preview unavailable
      </div>
    );
  }

  return (
    <button
      type='button'
      onClick={() => {
        track('Admin Message Attachment Preview Clicked', {
          attachment_id: attachment.id,
          attachment_type: attachment.attachment_type,
        });
        onImagePress(attachment);
      }}
      className='block w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset'
      title={`Preview ${attachment.name}`}
    >
      <img
        src={previewQuery.data}
        alt=''
        className='aspect-square w-full object-cover'
      />
    </button>
  );
}

function AdminAttachmentImageViewer({
  attachment,
  onClose,
}: {
  attachment: AdminMessageAttachmentRow;
  onClose: () => void;
}) {
  const { getToken } = useAuth();

  const fullQuery = useQuery({
    queryKey: ['admin', 'message-attachment-preview', attachment.id, 'full'],
    queryFn: () => fetchAdminImagePreview(getToken, previewPath(attachment.id, 'full')),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  useEffect(() => {
    const url = fullQuery.data;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fullQuery.data]);

  return (
    <button
      type='button'
      className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4'
      onClick={onClose}
      aria-label='Close image'
    >
      <FiX className='absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 text-white' />
      {fullQuery.isLoading ? (
        <div className='h-12 w-12 border-2 border-white border-t-transparent rounded-full animate-spin' />
      ) : fullQuery.data ? (
        <img
          src={fullQuery.data}
          alt={attachment.name}
          className='max-w-full max-h-full object-contain'
        />
      ) : (
        <p className='text-white text-sm'>Could not load preview.</p>
      )}
    </button>
  );
}

function AdminMessageAttachmentsPage() {
  const { getToken } = useAuth();
  const [page, setPage] = useState(0);
  const [householdFilter, setHouseholdFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [imagesOnly, setImagesOnly] = useState(false);
  const [viewerAttachment, setViewerAttachment] =
    useState<AdminMessageAttachmentRow | null>(null);

  useEffect(() => {
    track('Admin Message Attachments Screen');
  }, []);

  const householdIdParam = householdFilter.trim();
  const householdId =
    householdIdParam && /^\d+$/.test(householdIdParam)
      ? Number(householdIdParam)
      : null;

  const attachmentsQuery = useQuery({
    queryKey: [
      'admin',
      'message-attachments',
      page,
      householdIdParam,
      searchApplied,
      imagesOnly,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(page * PAGE_SIZE));
      if (householdId != null) params.set('household_id', String(householdId));
      if (searchApplied.trim()) params.set('q', searchApplied.trim());
      if (imagesOnly) params.set('images_only', 'true');
      return apiJson<AdminMessageAttachmentsListResponse>(
        getToken,
        `/v1/admin/message-attachments?${params.toString()}`,
      );
    },
    placeholderData: keepPreviousData,
  });

  const data = attachmentsQuery.data;
  const attachments = data?.attachments ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const applySearch = () => {
    setSearchApplied(searchQ);
    setPage(0);
  };

  return (
    <div className='space-y-8'>
      <div>
        <h1 className='text-2xl font-semibold text-gray-900'>
          Message attachments
        </h1>
        <p className='mt-2 text-sm text-gray-600'>
          Every file sent in household chat, newest first. Images render as
          previews; other files show their type.
        </p>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <button
          type='button'
          onClick={() => {
            void attachmentsQuery.refetch();
          }}
          className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
        >
          Refresh
        </button>
        {attachmentsQuery.isFetching ? (
          <span className='text-xs text-gray-500'>Updating…</span>
        ) : null}
      </div>

      <section className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
        <div className='px-4 py-3 border-b border-gray-200 space-y-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>All attachments</h2>
            <p className='text-xs text-gray-500 mt-1'>
              Filter by household, search file name or MIME type, or show images
              only.
            </p>
          </div>
          <div className='flex flex-wrap gap-4 items-end'>
            <div>
              <label className='block text-xs font-semibold text-gray-600 mb-1'>
                Household ID
              </label>
              <input
                value={householdFilter}
                onChange={(e) => {
                  setHouseholdFilter(e.target.value);
                  setPage(0);
                }}
                className='px-2 py-1 border border-gray-300 rounded text-sm w-28'
                placeholder='e.g. 12'
                inputMode='numeric'
              />
            </div>
            <div className='min-w-[12rem] flex-1 max-w-md'>
              <label className='block text-xs font-semibold text-gray-600 mb-1'>
                Search
              </label>
              <div className='flex gap-2'>
                <input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applySearch();
                  }}
                  className='flex-1 px-2 py-1 border border-gray-300 rounded text-sm'
                  placeholder='File name or MIME'
                />
                <button
                  type='button'
                  onClick={applySearch}
                  className='px-3 py-1 border border-gray-300 rounded text-sm font-semibold text-gray-700 hover:bg-gray-50'
                >
                  Apply
                </button>
              </div>
            </div>
            <label className='flex items-center gap-2 text-sm text-gray-700 pb-1'>
              <input
                type='checkbox'
                checked={imagesOnly}
                onChange={(e) => {
                  setImagesOnly(e.target.checked);
                  setPage(0);
                }}
                className='rounded border-gray-300'
              />
              Images only
            </label>
          </div>
        </div>

        {attachmentsQuery.isLoading && !data ? (
          <div className='flex justify-center py-16'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : attachmentsQuery.isError ? (
          <p className='px-4 py-6 text-sm text-red-600'>
            Could not load message attachments.
          </p>
        ) : attachments.length === 0 ? (
          <p className='px-4 py-6 text-sm text-gray-500'>
            No message attachments match these filters.
          </p>
        ) : (
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 p-4'>
            {attachments.map((attachment) => (
              <article
                key={attachment.id}
                className='overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm'
              >
                <AdminAttachmentImagePreview
                  attachment={attachment}
                  onImagePress={setViewerAttachment}
                />
                <div className='space-y-2 p-3'>
                  <p
                    className='font-medium text-gray-900 truncate'
                    title={attachment.name}
                  >
                    {attachment.name}
                  </p>
                  <p
                    className='text-xs text-gray-500 truncate'
                    title={attachment.attachment_type}
                  >
                    #{attachment.id} · {attachment.attachment_type}
                  </p>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span
                      className={`inline-block text-[10px] font-semibold uppercase px-2 py-0.5 rounded border ${typeBadgeClass(
                        attachment.chat_type,
                      )}`}
                    >
                      {typeLabel(attachment.chat_type)}
                    </span>
                    <span className='text-xs text-gray-600 truncate'>
                      {attachment.chat_title}
                    </span>
                  </div>
                  <p className='text-xs text-gray-600 tabular-nums'>
                    Household #{attachment.household_id} · message #
                    {attachment.message_id}
                  </p>
                  <div>
                    <p className='text-sm font-medium text-gray-900'>
                      {authorLabel(attachment.author, attachment.creator_user_id)}
                    </p>
                    {attachment.author.email ? (
                      <p
                        className='text-xs text-gray-500 truncate'
                        title={attachment.author.email}
                      >
                        {attachment.author.email}
                      </p>
                    ) : null}
                    <p className='text-xs text-gray-600 tabular-nums mt-1'>
                      {formatPacificDateTime(attachment.created_at)}
                    </p>
                    <Link
                      to='/admin/users/$userId'
                      params={{ userId: String(attachment.creator_user_id) }}
                      search={{ hid: String(attachment.household_id) }}
                      className={`${ADMIN_USER_LINK_CLASS} mt-2`}
                    >
                      User profile
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {total > 0 ? (
          <div className='flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm text-gray-600'>
            <span className='tabular-nums'>
              Showing {page * PAGE_SIZE + 1}–
              {Math.min(page * PAGE_SIZE + attachments.length, total)} of {total}
            </span>
            <div className='flex gap-2'>
              <button
                type='button'
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || attachmentsQuery.isFetching}
                className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
              >
                Prev
              </button>
              <span>
                Page {page + 1} / {totalPages}
              </span>
              <button
                type='button'
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages - 1 || attachmentsQuery.isFetching}
                className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </section>

      {viewerAttachment ? (
        <AdminAttachmentImageViewer
          attachment={viewerAttachment}
          onClose={() => setViewerAttachment(null)}
        />
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/message-attachments')({
  component: AdminMessageAttachmentsPage,
});
